// src/services/GoogleMapsService.js

/**
 * Service pour gérer l'intégration avec Google Maps API
 * Gère l'autocomplétion des adresses et la géolocalisation
 */
class GoogleMapsService {
  constructor() {
    this.isLoaded = false;
    this.loadPromise = null;
    this.autocompleteService = null;
    this.placesService = null;
    this.geocoder = null;
	
	// Configuration pour Douala
    this.DOUALA_CENTER = {
      lat: 4.0511,
      lng: 9.7679
    };
    this.SEARCH_RADIUS = 60000; // 40km en mètres
    
    // Types autorisés (rues et établissements uniquement)
    this.ALLOWED_TYPES = [
	  'route',
	  'street_address',
	  'establishment',
	  'point_of_interest',
	  'premise',
	  'street_number',
	  'restaurant',
	  'food',
	  'bar',
	  'cafe',
	  'store'
	];

	this.EXCLUDED_TYPES = [
	  'locality',
	  'sublocality',
	  'sublocality_level_1',
	  'administrative_area_level_1',
	  'administrative_area_level_2',
	  'country',
	  'political'
	];
  }

  /**
   * Charge la bibliothèque Google Maps
   * @returns {Promise<void>}
   */
  loadGoogleMaps() {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      if (this.isLoaded) {
        resolve();
        return;
      }

      // Vérifier si Google Maps est déjà chargé
      if (window.google && window.google.maps) {
        this.initializeServices();
        this.isLoaded = true;
        resolve();
        return;
      }

      // Récupération de la clé API depuis les variables d'environnement.
      // En CRA, toute variable REACT_APP_* est injectée dans le bundle au build.
      // La clé est volontairement exposée côté navigateur (API JS Google Maps
      // est conçue ainsi) mais elle DOIT être restreinte côté Google Cloud
      // Console (HTTP referrer + APIs autorisées + quotas).
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        const err = new Error(
          'Clé Google Maps absente : définis REACT_APP_GOOGLE_MAPS_API_KEY dans .env.local (dev) ou dans ton environnement de build (prod).'
        );
        // Remise à zéro pour permettre une nouvelle tentative après correction
        this.loadPromise = null;
        reject(err);
        return;
      }

      // Créer le script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=fr`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.initializeServices();
        this.isLoaded = true;
        resolve();
      };

      script.onerror = (error) => {
        // console.error('Erreur lors du chargement de Google Maps:', error);
        reject(new Error('Impossible de charger Google Maps'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Initialise les services Google Maps
   */
  initializeServices() {
    if (window.google && window.google.maps) {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
      this.geocoder = new window.google.maps.Geocoder();
      // Pour le PlacesService, nous avons besoin d'un élément div
      const serviceDiv = document.createElement('div');
      this.placesService = new window.google.maps.places.PlacesService(serviceDiv);
    }
  }

  /**
   * Recherche des suggestions d'adresses
   * @param {string} input - Le texte saisi par l'utilisateur
   * @returns {Promise<Array>} - Liste des prédictions
   */
  // src/services/GoogleMapsService.js

	async getPlacePredictions(input) {
	  if (!input || input.trim().length < 3) {
		return [];
	  }

	  await this.loadGoogleMaps();

	  return new Promise(async (resolve, reject) => {
		const location = new window.google.maps.LatLng(this.DOUALA_CENTER.lat, this.DOUALA_CENTER.lng);

		const requestAddress = {
		  input,
		  componentRestrictions: { country: 'cm' },
		  types: ['address'],
		  language: 'fr',
		  location,
		  radius: this.SEARCH_RADIUS,
		  strictBounds: false
		};

		const requestEstablishment = {
		  input,
		  componentRestrictions: { country: 'cm' },
		  types: ['establishment'],
		  language: 'fr',
		  location,
		  radius: this.SEARCH_RADIUS,
		  strictBounds: false
		};

		try {
		  // Lancer les deux requêtes en parallèle
		  const [addressPredictions, establishmentPredictions] = await Promise.all([
			  new Promise((res) => {
				this.autocompleteService.getPlacePredictions(requestAddress, (predictions, status) => {
				  // console.log('📍 Adresses retournées:', status, predictions); // 🔍 DEBUG
				  res(status === 'OK' ? predictions : []);
				});
			  }),
			  new Promise((res) => {
				this.autocompleteService.getPlacePredictions(requestEstablishment, (predictions, status) => {
				  // console.log('🏢 Établissements retournés:', status, predictions); // 🔍 DEBUG
				  res(status === 'OK' ? predictions : []);
				});
			  })
			]);

		  // Fusionner avec un type personnalisé
		  const combined = [
			...addressPredictions.map(p => ({ ...p, resultType: 'address' })),
			...establishmentPredictions.map(p => ({ ...p, resultType: 'establishment' }))
		  ];

		  // Supprimer les doublons (même description)
		  const seen = new Set();
		  const filteredDuplicates = combined.filter(pred => {
			if (seen.has(pred.description)) return false;
			seen.add(pred.description);
			return true;
		  });

		  // Appliquer ton filtre existant
		  const finalPredictions = this.filterPredictions(filteredDuplicates);

		  // Limiter à 6 résultats max
		  resolve(finalPredictions.slice(0, 6));

		} catch (error) {
		  // console.error('Erreur dans getPlacePredictions:', error);
		  resolve([]);
		}
	  });
	}

  /**
   * Filtre les prédictions pour exclure les villes et résultats trop généraux
   * @param {Array} predictions - Liste des prédictions Google Places
   * @returns {Array} - Liste filtrée des prédictions
   */
	  filterPredictions(predictions) {
	  return predictions.filter(prediction => {
		const types = prediction.types || [];
		
		// Exclure les types trop généraux
		const hasExcludedType = types.some(type => this.EXCLUDED_TYPES.includes(type));
		if (hasExcludedType) {
		  return false;
		}

		// Exclure les villes seules
		const description = prediction.description.toLowerCase();
		const isJustCity = 
		  description === 'douala, cameroun' || 
		  description === 'yaoundé, cameroun' ||
		  /^[^,]+,\s*cameroun$/i.test(description);
		
		if (isJustCity) {
		  return false;
		}

		return true;
	  });
	}

  /**
   * Obtient les détails complets d'un lieu
   * @param {string} placeId - L'ID du lieu Google
   * @returns {Promise<Object>} - Détails du lieu
   */
  async getPlaceDetails(placeId) {
    await this.loadGoogleMaps();

    return new Promise((resolve, reject) => {
      const request = {
        placeId: placeId,
        fields: ['name', 'formatted_address', 'geometry', 'address_components'],
        language: 'fr'
      };

      this.placesService.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const details = {
            name: place.name,
            address: place.formatted_address,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            addressComponents: place.address_components
          };
          resolve(details);
        } else {
          // console.error('Erreur PlacesService:', status);
          reject(new Error('Impossible de récupérer les détails du lieu'));
        }
      });
    });
  }

  /**
   * Géocode une adresse pour obtenir ses coordonnées
   * @param {string} address - L'adresse à géocoder
   * @returns {Promise<Object>} - Coordonnées GPS
   */
  async geocodeAddress(address) {
    await this.loadGoogleMaps();

    return new Promise((resolve, reject) => {
      this.geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            latitude: location.lat(),
            longitude: location.lng(),
            formattedAddress: results[0].formatted_address
          });
        } else {
          // console.error('Erreur de géocodage:', status);
          reject(new Error('Impossible de géocoder l\'adresse'));
        }
      });
    });
  }

  /**
   * Calcule la distance entre deux points GPS
   * @param {number} lat1 - Latitude du premier point
   * @param {number} lng1 - Longitude du premier point
   * @param {number} lat2 - Latitude du second point
   * @param {number} lng2 - Longitude du second point
   * @returns {number} - Distance en kilomètres
   */
  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Rayon de la Terre en kilomètres
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convertit des degrés en radians
   * @param {number} deg - Valeur en degrés
   * @returns {number} - Valeur en radians
   */
  toRad(deg) {
    return deg * (Math.PI / 180);
  }
}

// Exporter une instance unique du service
export default new GoogleMapsService();